import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map, has, startsWith, findIndex, isObject } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import ProductApi from '@/extensions/shop/store/api/product';
import CategoryApi from '@/extensions/shop/store/api/category';
import PropertyApi from '@/extensions/shop/store/api/property';
import PropSetApi from '@/extensions/shop/store/api/propSet';
import PropTypeApi from '@/extensions/shop/store/api/propType';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import StockApi from '@/extensions/shop/store/api/stock';
import TaxApi from '@/extensions/shop/store/api/tax';
import TagApi from '@/extensions/shop/store/api/tag';
import MenuApi from '@/store/api/cms/menu';
import IndicatorApi from '@/store/api/system/indicator';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

const defaultForm = {
    nr: '',
    type: 'product',
    status: 'enabled',
    condition: 'new',
    brand: '',
    name: '',
    teaser: '',
    description: '',
    unit: undefined,
    quantity: undefined,
    shipping_points: undefined,
    pos_favorite: false,
    manage_stock: false,
    out_of_stock_threshold: 0,
    returnable: true,
    tags: [],
    memberTags: [],
    media: [],
    suppliers: [],
    categories: [],
    page: null,
    propSet: undefined,
    propertyPivots: [],
    prices: []
};

const defaultUi = {
    pagination: {},
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useShopProductStore = defineStore('shopProduct', {
    state: () => ({
        loading: true,
        productsPaginated: {
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            status: {
                loading: false,
                items: [
                    { value: 'enabled', label: 'Enabled' },
                    { value: 'disabled', label: 'Disabled' }
                ]
            },
            condition: {
                loading: false,
                items: [
                    { value: 'new', label: 'New' },
                    { value: 'refurbished', label: 'Refurbished' },
                    { value: 'used', label: 'Used' },
                ]
            },
            type: {
                loading: false,
                // 'event' is appended in the getter below — it only makes sense when the event
                // manager is switched on for this CMS.
                items: [
                    { value: 'product', label: 'Produkt' },
                    { value: 'coupon', label: 'Coupon' }
                ]
            },
            priceType: {
                loading: false,
                items: [
                    { value: 'main', label: 'Standard' },
                    { value: 'group', label: 'Kundengruppenpreis' },
                    { value: 'sale', label: 'Angebotspreis' },
                    { value: 'scaled', label: 'Mengenrabatt' }
                ]
            },
            propSet: {
                loading: false,
                items: []
            },
            propType: {
                loading: false,
                items: []
            },
            property: {
                loading: false,
                items: []
            },
            dateType: {
                loading: false,
                items: [
                    { value: 'datetime', label: 'Datum, Uhrzeit' },
                    { value: 'date', label: 'Datum' },
                    { value: 'week', label: 'Woche' },
                    { value: 'month', label: 'Monat' },
                    { value: 'year', label: 'Jahr' }
                ]
            },
            customerGroup: {
                loading: false,
                items: []
            },
            category: {
                loading: false,
                items: []
            },
            tax: {
                loading: false,
                items: []
            },
            stock: {
                loading: false,
                items: []
            },
            menu: {
                loading: false,
                items: []
            },
            tag: {
                loading: false,
                items: []
            },
            indicator: {
                loading: false,
                items: []
            },
            extensions: {
                restrictedArea: {
                    memberTag: {
                        loading: false,
                        items: []
                    },
                }
            }
        },
        initialProduct: cloneDeep(defaultForm),
        form: cloneDeep(defaultForm),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter'),
        showRelatedArticles: () => false,
        // a single-article product owns all shared data (api docs/features/PRODUCT_ARTICLE_INHERITANCE.md)
        isSingleArticleProduct: (state) => get(state, 'form.articleOverrides.single_article') === true,

        /**
         * Selling tickets needs both halves: the shop that takes the money and the event manager
         * that owns the dates. The backend refuses the type without both, so do not offer it.
         */
        productTypes: (state) => {
            const setupStore = useSetupStore(pinia);
            const types = [...get(state, 'options.type.items', [])];

            if (setupStore.enabledExtensions.includes('event-manager')) {
                types.push({ value: 'event', label: 'Veranstaltungsticket' });
            }

            return types;
        },
        rules: (state) => {
            const setupStore = useSetupStore(pinia);
            let rules = {
                nr: 'required',
                status: 'required',
                condition: 'required',
                brand: 'max:70',
                name: 'required|min:3',
                propSet: { id: 'required' },
                prices: []
            };

            // Neither a coupon nor a ticket has product properties, a condition or a manufacturer,
            // so none of those fields are rendered for them. Leaving the rules in place would block
            // Save forever on fields that are never on screen to fix.
            if (['coupon', 'event'].includes(state.form.type)) {
                delete rules.propSet;
                delete rules.condition;
                delete rules.brand;
            }

            const shop = setupStore.activeCms.shop;
            const product = state.form;

            if (product.id) {
                Vue.set(rules, 'nr', `required|unique_product_nr:"shop":${shop.id},"product":${product.id}`);
                Vue.set(rules, 'slug', `required|unique_product_slug:"shop":${shop.id},"product":${product.id}`);
            } else {
                Vue.set(rules, 'nr', `required|unique_product_nr:"shop":${shop.id}`);
                Vue.set(rules, 'slug', `required|unique_product_slug:"shop":${shop.id}`);
            }

            Vue.set(rules, 'prices', []);

            get(product, 'prices', []).forEach((price, index) => {
                Vue.set(rules.prices, index, {
                    type: 'required',
                    value: 'required|min_value:0',
                    min_qty: 'required|integer|min_value:1',
                    start_at: 'required',
                    end_at: 'required',
                    customerGroup: { id: 'required' }
                });
            });

            return rules;
        }
    },

    actions: {
        addPaginatedProductsToState(data) {
            this.productsPaginated.items = data.items;
            this.productsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.productsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.productsPaginated.link = cloneDeep(data.config.link);
            this.productsPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.productsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.productsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.productsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.productsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.productsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.productsPaginated.filters, key);
            } else {
                Vue.set(this.productsPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            Vue.set(this.productsPaginated.variables, 'order', key);
            Vue.set(this.productsPaginated.variables, 'direction', value);
        },

        paginationBulkSelectionCheckAll({ value }) {
            let selection = this.productsPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.productsPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        paginationBulkSelectionCheckVisible({ value }) {
            const items = get(this.productsPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.productsPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            let selection = this.productsPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.productsPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        updateLoading(value) {
            this.loading = value;
        },

        defineFilterVisibility(value) {
            Vue.set(this.productsPaginated, 'showFilters', value);
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            // moved to getters
        },

        resetUI() {
            Vue.set(this.ui.form, 'initialized', false);
        },

        insertProduct(product) {
            if (!product.status) {
                product.status = 'enabled';
            }
            Vue.set(this, 'form', product);
            this.initialProduct = Object.freeze(cloneDeep(product));

            // A freshly loaded product may carry no property set, which would fail the required
            // `propSet.id` rule on save. Preselect the default here too - the prop-set options and the
            // product load in parallel, so whichever finishes last must apply the default (see
            // applyDefaultPropSet / fetchPropSetOptions).
            this.applyDefaultPropSet();
        },

        updatePropSetOptionsLoading(value) {
            Vue.set(this.options.propSet, 'loading', value);
        },

        insertPropSetOptions(propSets) {
            Vue.set(this.options.propSet, 'items', propSets);
        },

        updatePropTypeOptionsLoading(value) {
            Vue.set(this.options.propType, 'loading', value);
        },

        insertPropTypeOptions(propTypes) {
            Vue.set(this.options.propType, 'items', propTypes);
        },

        updatePropertyOptionsLoading(value) {
            Vue.set(this.options.property, 'loading', value);
        },

        insertPropertyOptions(properties) {
            Vue.set(this.options.property, 'items', properties);
        },

        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        updateCategoryOptionsLoading(value) {
            Vue.set(this.options.category, 'loading', value);
        },

        insertCategoryOptions(categories) {
            Vue.set(this.options.category, 'items', categories);
        },

        updateStockOptionsLoading(value) {
            Vue.set(this.options.stock, 'loading', value);
        },

        insertStockOptions(stocks) {
            Vue.set(this.options.stock, 'items', stocks);
        },

        updateTaxOptionsLoading(value) {
            Vue.set(this.options.tax, 'loading', value);
        },

        insertTaxOptions(taxes) {
            Vue.set(this.options.tax, 'items', taxes);
        },

        updateMenuOptionsLoading(value) {
            Vue.set(this.options.menu, 'loading', value);
        },

        insertMenuOptions(menus) {
            Vue.set(this.options.menu, 'items', menus);
        },

        updateTagOptionsLoading(value) {
            Vue.set(this.options.tag, 'loading', value);
        },

        insertTagOptions(tags) {
            Vue.set(this.options.tag, 'items', tags);
        },

        updateIndicatorOptionsLoading(value) {
            Vue.set(this.options.indicator, 'loading', value);
        },

        insertIndicatorOptions(items) {
            Vue.set(this.options.indicator, 'items', items);
        },

        // restricted area
        updateRestrictedAreaMemberTagOptionsLoading(value) {
            Vue.set(this.options.extensions.restrictedArea.memberTag, 'loading', value);
        },

        insertRestrictedAreaMemberTagOptions(menus) {
            Vue.set(this.options.extensions.restrictedArea.memberTag, 'items', menus);
        },

        // form
        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        addFormMedia(media) {
            const index = get(this, 'form.media.length', 0);
            Vue.set(this.form.media, index, media);
        },

        sortFormMedia(media) {
            const currentMedia = get(this.form, `media`) || [];
            media.forEach((item, index) => {
                const m = findIndex(currentMedia, { id: item.id });
                if (m >= 0) {
                    Vue.set(this.form.media[m], 'position', index + 1);
                }
            });
        },

        markFormMediaAsDeleted(i) {
            Vue.set(this.form.media[i], 'deleted', true);
        },

        unmarkFormMediaAsDeleted(i) {
            Vue.set(this.form.media[i], 'deleted', false);
        },

        updateFormMediaVisibility({ media, scope, value }) {
            const currentMedia = get(this.form, `media`) || [];
            const m = findIndex(currentMedia, { id: media.id });
            if (m >= 0) {
                if (this.form.media[m].settings === undefined) Vue.set(this.form.media[m], 'settings', {});
                if (this.form.media[m].settings.show === undefined) Vue.set(this.form.media[m].settings, 'show', {});
                Vue.set(this.form.media[m].settings.show, scope, value);
            }
        },

        // Art. 50 fields on a media VIDEO. Written here, not on the tile component: the
        // media objects come straight out of the GraphQL result and carry no Vue
        // observer, so assigning on them changes the value but re-renders nothing - the
        // switch stayed put and the badge never lit up. Vue.set against the store's own
        // form array is what makes it reactive, exactly like updateFormMediaVisibility.
        updateFormMediaVideoAi({ media, key, value }) {
            const currentMedia = get(this.form, `media`) || [];
            const m = findIndex(currentMedia, { id: media.id });
            if (m < 0 || !this.form.media[m].video) return;

            Vue.set(this.form.media[m].video, key, value === undefined ? null : value);

            // Turning the disclosure off clears the kind with it, so a video is never
            // left carrying a term it no longer claims.
            if (key === 'ai_disclosure' && value !== true) {
                Vue.set(this.form.media[m].video, 'ai_source_type', null);
            }
        },

        setFormNr(value) {
            Vue.set(this.form, 'nr', value);
        },

        setFormCondition(value) {
            Vue.set(this.form, 'condition', value);
        },

        setFormBrand(value) {
            Vue.set(this.form, 'brand', value);
        },

        setFormStatus(value) {
            Vue.set(this.form, 'status', value);
        },

        setFormName(value) {
            Vue.set(this.form, 'name', value);
        },

        setFormSlug(value) {
            Vue.set(this.form, 'slug', value);
        },

        setFormTeaser(value) {
            Vue.set(this.form, 'teaser', value);
        },

        setFormDescription(value) {
            Vue.set(this.form, 'description', value);
        },

        setFormUnit(value) {
            Vue.set(this.form, 'unit', value);
        },

        setFormQuantity(value) {
            Vue.set(this.form, 'quantity', value);
        },

        setFormPosition(value) {
            Vue.set(this.form, 'position', value);
        },

        setFormShippingPoints(value) {
            Vue.set(this.form, 'shipping_points', value);
        },

        setFormPosFavorite(value) {
            Vue.set(this.form, 'pos_favorite', value);
        },

        setFormRelatedArticles(relatedArticles) {
            Vue.set(this.form, 'relatedArticles', relatedArticles);
        },

        setFormRestriction(restriction) {
            Vue.set(this.form, 'restriction', restriction);
        },

        setFormReturnable(returnable) {
            Vue.set(this.form, 'returnable', !!returnable);
        },

        setFormHiddenInCmsIds(hiddenInCmsIds) {
            Vue.set(this.form, 'hidden_in_cms_ids', hiddenInCmsIds || []);
        },

        setFormTags(tags) {
            Vue.set(this.form, 'tags', tags);
        },

        setFormMemberTags(memberTags) {
            Vue.set(this.form, 'memberTags', memberTags);
        },

        setFormIndicators(value) {
            Vue.set(this.form, 'indicators', value);
        },

        setFormManageStock(value) {
            Vue.set(this.form, 'manage_stock', value);
        },

        setFormOutOfStockThreshold(value) {
            Vue.set(this.form, 'out_of_stock_threshold', value);
        },

        setFormCategories(value) {
            Vue.set(this.form, 'categories', value);
        },

        setFormSlots(value) {
            Vue.set(this.form, 'slots', value);
        },

        setFormPage(value) {
            Vue.set(this.form, 'page', value);
        },

        setFormPropSet(value) {
            Vue.set(this.form, 'propSet', value);
        },

        setFormPropertyPivot(value) {
            const i = this.form.propertyPivots.findIndex(el => el.id === value.id);
            if (i >= 0) Vue.set(this.form.propertyPivots, i, value);
        },

        setFormPropertyPivots(value) {
            Vue.set(this.form, 'propertyPivots', value);
        },

        markFormPropertyPivotAsDeleted(i) {
            Vue.set(this.form.propertyPivots[i], 'deleted', true);
        },

        unmarkFormPropertyPivotAsDeleted(i) {
            Vue.set(this.form.propertyPivots[i], 'deleted', false);
        },

        addFormPrice(price) {
            const index = get(this, 'form.prices.length', 0);
            if (!price.id) price.id = uniqid('new:');
            Vue.set(this.form.prices, index, price);
        },

        updateFormPrice({ price, field, value }) {
            const prices = get(this.form, 'prices') || [];
            const index = findIndex(prices, { id: price.id });

            switch (field) {
                case 'tax':
                case 'customerGroup':
                    if (!isObject(value)) value = null;
                    break;
                case 'value':
                case 'net_value':
                case 'price':
                case 'min_qty':
                    if (isNaN(value)) value = null;
                    break;
            }

            if (index >= 0) {
                Vue.set(this.form.prices[index], field, value);
            }
        },

        deleteFormPrice({ index }) {
            Vue.delete(this.form.prices, index);
        },

        addFormTranslation(translation) {
            if (this.form.translationOrigin) {
                const translations = get(this.form.translationOrigin, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i < 0) i = translations.length;
                Vue.set(this.form.translationOrigin.translations, i, translation);
            } else {
                const translations = get(this.form, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i < 0) i = translations.length;
                Vue.set(this.form.translations, i, translation);
            }
        },

        removeFormTranslation(translation) {
            if (this.form.translationOrigin) {
                const translations = get(this.form.translationOrigin, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i >= 0) Vue.delete(this.form.translationOrigin.translations, i);
            } else {
                const translations = get(this.form, 'translations', []);
                let i = translations.findIndex(el => el.language.code === translation.language.code);
                if (i >= 0) Vue.delete(this.form.translations, i);
            }
        },

        // Async actions
        fetchProductsPaginated() {
            return ProductApi.fetchProductsPaginated(this.productsPaginated).then(data => {
                this.addPaginatedProductsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        processPaginationBulkAction(action, options) {
            return ProductApi.processProductPaginationBulkAction(action, this.productsPaginated, options);
        },

        fetchProduct(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (cms && cms.shop && cms.shop.id) {
                return ProductApi.fetchProduct({ id, shop: cms.shop.id })
                    .then(product => {
                        this.insertProduct(cloneDeep(product));
                    });
            }
        },

        // An article save changes which articles override the product: refresh only those counts,
        // so unsaved edits on the product form stay (api docs/features/PRODUCT_ARTICLE_INHERITANCE.md)
        refreshArticleOverrides() {
            const setupStore = useSetupStore(pinia);
            const shopId = get(setupStore, 'activeCms.shop.id');
            if (!this.form.id || !shopId) return Promise.resolve();

            return ProductApi.fetchArticleOverrides(this.form.id, shopId)
                .then(articleOverrides => {
                    this.form.articleOverrides = articleOverrides;
                });
        },

        fetchPropSetOptions(shopID) {
            if (this.form.propSet) this.insertPropSetOptions([this.form.propSet]);

            this.updatePropSetOptionsLoading(true);
            return PropSetApi.fetchList(shopID)
                .then(propSets => {
                    this.updatePropSetOptionsLoading(false);
                    this.insertPropSetOptions(propSets);
                    this.applyDefaultPropSet();
                })
                .catch(err => {
                    this.updatePropSetOptionsLoading(false);
                    throw err;
                });
        },

        // The property set is mandatory for regular products (rule `propSet.id: required`), but its
        // picker only lives on the "Bestand & Individualisierung" tab (Properties.vue). Without this
        // the default was assigned in that component's mounted() hook, which never runs until the tab
        // is opened - so saving failed with "Formular ist ungültig!" until the user visited the tab.
        // Preselect the first available set as soon as the options are loaded, independent of the tab.
        applyDefaultPropSet() {
            if (['coupon', 'event'].includes(this.form.type)) return;
            if (this.form.propSet) return;

            const items = this.options.propSet.items;
            if (items && items.length) this.setFormPropSet(items[0]);
        },

        fetchPropTypeOptions() {
            this.updatePropTypeOptionsLoading(true);
            return PropTypeApi.fetchList()
                .then(propTypes => {
                    this.updatePropTypeOptionsLoading(false);
                    this.insertPropTypeOptions(propTypes);
                })
                .catch(err => {
                    this.updatePropTypeOptionsLoading(false);
                    throw err;
                });
        },

        fetchPropertyOptions(shopID) {
            this.updatePropertyOptionsLoading(true);
            return PropertyApi.fetchList(shopID)
                .then(properties => {
                    this.updatePropertyOptionsLoading(false);
                    this.insertPropertyOptions(properties);
                })
                .catch(err => {
                    this.updatePropertyOptionsLoading(false);
                    throw err;
                });
        },

        fetchCustomerGroupOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeShop.defaultCustomerGroup'))
                this.insertCustomerGroupOptions([setupStore.activeShop.defaultCustomerGroup]);

            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(shopID)
                .then(customerGroups => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch(err => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        fetchCategoryOptions(shopID) {
            this.updateCategoryOptionsLoading(true);
            return CategoryApi.fetchList(shopID)
                .then(categories => {
                    this.updateCategoryOptionsLoading(false);
                    this.insertCategoryOptions(categories);
                })
                .catch(err => {
                    this.updateCategoryOptionsLoading(false);
                    throw err;
                });
        },

        fetchStockOptions(shopID) {
            this.updateStockOptionsLoading(true);
            return StockApi.fetchList(shopID)
                .then(stocks => {
                    this.updateStockOptionsLoading(false);
                    this.insertStockOptions(stocks);
                })
                .catch(err => {
                    this.updateStockOptionsLoading(false);
                    throw err;
                });
        },

        fetchTaxOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeShop.defaultTax'))
                this.insertTaxOptions([setupStore.activeShop.defaultTax]);

            this.updateTaxOptionsLoading(true);
            return TaxApi.fetchList(shopID)
                .then(taxes => {
                    this.updateTaxOptionsLoading(false);
                    this.insertTaxOptions(taxes);
                })
                .catch(err => {
                    this.updateTaxOptionsLoading(false);
                    throw err;
                });
        },

        fetchMenuOptions(cmsID) {
            this.updateMenuOptionsLoading(true);
            return MenuApi.fetchList(cmsID)
                .then(menus => {
                    this.updateMenuOptionsLoading(false);
                    this.insertMenuOptions(menus);
                })
                .catch(err => {
                    this.updateMenuOptionsLoading(false);
                    throw err;
                });
        },

        fetchTagOptions(cmsID) {
            this.updateTagOptionsLoading(true);
            return TagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateTagOptionsLoading(false);
                    this.insertTagOptions(tags);
                })
                .catch(err => {
                    this.updateTagOptionsLoading(false);
                    throw err;
                });
        },

        fetchIndicatorOptions() {
            this.updateIndicatorOptionsLoading(true);
            return IndicatorApi.fetchList()
                .then(indicators => {
                    this.updateIndicatorOptionsLoading(false);
                    this.insertIndicatorOptions(indicators);
                })
                .catch(err => {
                    this.updateIndicatorOptionsLoading(false);
                    throw err;
                });
        },

        // restricted area
        fetchRestrictedAreaMemberTagOptions(cmsID) {
            this.updateRestrictedAreaMemberTagOptionsLoading(true);
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateRestrictedAreaMemberTagOptionsLoading(false);
                    this.insertRestrictedAreaMemberTagOptions(tags);
                })
                .catch(err => {
                    this.updateRestrictedAreaMemberTagOptionsLoading(false);
                    throw err;
                });
        },

        propertyAutocomplete({ shopID, propID, valueField, query, limit }) {
            return PropertyApi.autocomplete(shopID, propID, valueField, query, limit);
        },

        initializeProductImport({ shopID, file }) {
            return ProductApi.initializeImport(shopID, file);
        },

        validateProductImportCsv({ shopID, file }) {
            return ProductApi.validateImportCsv(shopID, file);
        },

        translate({ product, language, properties, type }) {
            return ProductApi.translate(product, language, properties, type);
        },

        save(product) {
            product = cloneDeep(product);
            if (product.propertyPivots && product.propertyPivots.length) {
                product.propertyPivots.map(el => {
                    if (el.property && el.property.default !== undefined) {
                        delete el.property.default;
                    }
                });
            }

            if (!product.id || startsWith(product.id, 'new:')) {
                return this.create(product);
            } else {
                return this.update(product);
            }
        },

        create(product) {
            return ProductApi.create(product).then(async product => {
                await this.fetchRestrictedAreaMemberTagOptions();
            });
        },

        update(product) {
            let translation = false;
            if (product.translationOrigin) translation = true;
            return ProductApi.update(product, translation).then(async product => {
                await this.fetchRestrictedAreaMemberTagOptions();
                this.insertProduct(cloneDeep(product));
            });
        },

        delete(id) {
            return ProductApi.delete(parseInt(id)).then(() => {
                this.reset();
                this.resetUI();
            });
        },

        dissolveByProperty(productId, propertyId) {
            return ProductApi.dissolveByProperty(productId, propertyId);
        },

        cloneProduct(productId) {
            return ProductApi.cloneProduct(productId);
        },

        reset() {
            this.insertProduct(cloneDeep(defaultForm));
        }
    }
});
